import { supabase } from '../lib/supabase';

const ratingsService = {
  // Get user reputation with trust badges
  async getUserReputationWithBadges(userId) {
    try {
      const [reputationResult, badgesResult, reviewsResult] = await Promise.all([
        supabase
          ?.from('user_reputation')
          ?.select('*')
          ?.eq('user_id', userId)
          ?.single(),
        supabase
          ?.from('trust_badges')
          ?.select('*')
          ?.eq('user_id', userId)
          ?.eq('is_active', true)
          ?.order('awarded_at', { ascending: false }),
        supabase
          ?.from('user_reviews')
          ?.select(`
            *,
            reviewer:reviewer_id(id, full_name, avatar_url)
          `)
          ?.eq('reviewed_user_id', userId)
          ?.order('created_at', { ascending: false })
          ?.limit(20)
      ]);

      const reputation = reputationResult?.data || {
        user_id: userId,
        total_sales: 0,
        total_purchases: 0,
        seller_rating: 0,
        buyer_rating: 0,
        successful_trades: 0,
        total_seller_reviews: 0,
        total_buyer_reviews: 0
      };

      return {
        data: {
          reputation,
          badges: badgesResult?.data || [],
          reviews: reviewsResult?.data || []
        },
        error: null
      };
    } catch (error) {
      console.error('Error fetching user reputation with badges:', error);
      return { data: null, error: error?.message || 'Failed to fetch reputation' };
    }
  },

  // Submit a review for a user
  async submitReview(reviewData) {
    try {
      const { data, error } = await supabase
        ?.from('user_reviews')
        ?.insert([reviewData])
        ?.select()
        ?.single();

      if (error) throw error;

      // Trigger badge award check
      await supabase?.rpc('award_trust_badges', { p_user_id: reviewData?.reviewed_user_id });

      return { data, error: null };
    } catch (error) {
      console.error('Error submitting review:', error);
      return { data: null, error: error?.message || 'Failed to submit review' };
    }
  },

  // Get reviews for a user
  async getUserReviews(userId, role = null) {
    try {
      let query = supabase
        ?.from('user_reviews')
        ?.select(`
          *,
          reviewer:reviewer_id(id, full_name, avatar_url)
        `)
        ?.eq('reviewed_user_id', userId)
        ?.order('created_at', { ascending: false });

      if (role) {
        query = query?.eq('role', role);
      }

      const { data, error } = await query;
      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching user reviews:', error);
      return { data: [], error: error?.message || 'Failed to fetch reviews' };
    }
  },

  // Get trust badges for a user
  async getUserBadges(userId) {
    try {
      const { data, error } = await supabase
        ?.from('trust_badges')
        ?.select('*')
        ?.eq('user_id', userId)
        ?.eq('is_active', true)
        ?.order('awarded_at', { ascending: false });

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching trust badges:', error);
      return { data: [], error: error?.message || 'Failed to fetch badges' };
    }
  },

  // Check if user has already reviewed another user for a trade
  async hasReviewed(reviewerId, reviewedUserId, tradeHistoryId) {
    try {
      const { data, error } = await supabase
        ?.from('user_reviews')
        ?.select('id')
        ?.eq('reviewer_id', reviewerId)
        ?.eq('reviewed_user_id', reviewedUserId)
        ?.eq('trade_history_id', tradeHistoryId)
        ?.single();

      return { hasReviewed: !!data, error: null };
    } catch {
      return { hasReviewed: false, error: null };
    }
  }
};

export default ratingsService;
